import React, { useState } from 'react';
import { useGetAccountManagerDashboard } from '@workspace/api-client-react';
import { 
  Users, 
  Ticket, 
  Clock, 
  Briefcase,
  MapPin,
  ListOrdered,
  Search,
  ArrowRightLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

export function AccountManagerDashboard() {
  const { data: dashboard, isLoading, error } = useGetAccountManagerDashboard();
  const [mode, setMode] = useState<'region' | 'round-robin'>('region');

  if (isLoading) {
    return <div className="animate-pulse space-y-6"><div className="h-20 bg-gray-200 rounded"></div><div className="h-64 bg-gray-200 rounded"></div></div>;
  }

  if (error || !dashboard) {
    return <div className="text-red-500">Failed to load dashboard.</div>;
  }

  return (
    <div className="space-y-8">
      {/* Top Controls & Mode Toggle */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Current Assignment Mode</span>
            <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200">
              <button
                onClick={() => setMode('region')}
                className={cn(
                  "px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2",
                  mode === 'region' ? "bg-white text-[#0B132B] shadow-sm border border-slate-200/50" : "text-slate-500 hover:text-slate-700"
                )}
              >
                <MapPin className="w-4 h-4" />
                Region & Area
              </button>
              <button
                onClick={() => setMode('round-robin')}
                className={cn(
                  "px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2",
                  mode === 'round-robin' ? "bg-white text-[#0B132B] shadow-sm border border-slate-200/50" : "text-slate-500 hover:text-slate-700"
                )}
              >
                <ListOrdered className="w-4 h-4" />
                Round-Robin
              </button>
            </div>
          </div>
        </div>
        
        <Button className="bg-[#E6007E] hover:bg-[#E6007E]/90 text-white shadow-sm font-medium">
          <ArrowRightLeft className="w-4 h-4 mr-2" />
          Rebalance Assignments
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5 flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-500 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Workload Score</span>
            <span className="text-2xl font-bold text-[#0B132B]">{dashboard.workloadScore}%</span>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5 flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-500 flex items-center gap-2"><Users className="w-4 h-4" /> Managed Agents</span>
            <span className="text-2xl font-bold text-[#0B132B]">{dashboard.totalAgentsManaged.toLocaleString()}</span>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5 flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-500 flex items-center gap-2"><Ticket className="w-4 h-4" /> Open Tickets</span>
            <span className="text-2xl font-bold text-red-600">{dashboard.openTickets}</span>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5 flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-500 flex items-center gap-2"><Clock className="w-4 h-4" /> Avg Resolution</span>
            <span className="text-2xl font-bold text-[#0B132B]">{dashboard.avgResolutionDays} days</span>
          </CardContent>
        </Card>
      </div>

      {/* Regions List */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4 border-b border-slate-100 bg-slate-50/80 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          <div className="col-span-6 pl-2">Region</div>
          <div className="col-span-4 text-center">Code</div>
          <div className="col-span-2 text-right pr-4">Agents</div>
        </div>
        
        <div className="divide-y divide-slate-100">
          {dashboard.regions.map((region) => (
            <div key={region.id} className="grid grid-cols-12 gap-4 p-4 items-center transition-colors hover:bg-slate-50/50">
              
              <div className="col-span-6 flex items-center gap-3 pl-2">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <div className="font-semibold text-[#0B132B]">{region.name}</div>
                </div>
              </div>

              <div className="col-span-4 text-center text-sm font-medium text-slate-600">
                {region.code}
              </div>

              <div className="col-span-2 text-right font-medium text-[#0B132B] pr-4">
                {region.agentCount || 0}
              </div>
            </div>
          ))}

          {dashboard.regions.length === 0 && (
            <div className="p-8 text-center text-slate-500">No regions assigned.</div>
          )}
        </div>
      </div>
    </div>
  );
}
